import React, { useCallback, useEffect, useState, useRef } from 'react'
import { useNavigate, useParams } from 'react-router-dom'
import toast, { Toaster } from 'react-hot-toast'
import {
  workflowApi,
  WorkflowDetail,
  WorkflowExecution,
  WorkflowAuditLog,
} from '../../lib/workflowApi'
import {
  ArrowLeft,
  Play,
  Pause,
  Edit,
  Trash2,
  Clock,
  Mail,
  Webhook,
  Rss,
  Zap,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Copy,
  ChevronDown,
  ChevronUp,
  RefreshCw,
  History,
  FileText,
} from 'lucide-react'

// Formatters
const numberFormatter = new Intl.NumberFormat('fr-FR')
const dateFormatter = new Intl.DateTimeFormat('fr-FR', {
  dateStyle: 'medium',
  timeStyle: 'short',
})

// Icônes des connecteurs
const connectorIcons: Record<string, React.ReactNode> = {
  schedule: <Clock className="w-4 h-4" />,
  manual: <Play className="w-4 h-4" />,
  webhook: <Webhook className="w-4 h-4" />,
  email: <Mail className="w-4 h-4" />,
  email_imap: <Mail className="w-4 h-4" />,
  rss_feed: <Rss className="w-4 h-4" />,
  slack: <Zap className="w-4 h-4" />,
  webhook_out: <Webhook className="w-4 h-4" />,
}

// Noms français des connecteurs
const connectorNames: Record<string, string> = {
  schedule: 'Planification',
  manual: 'Manuel',
  webhook: 'Webhook',
  email: 'Email',
  email_imap: 'Email IMAP',
  rss_feed: 'Flux RSS',
  slack: 'Slack',
  webhook_out: 'Webhook sortant',
}

type Tab = 'executions' | 'audit'

export default function WorkflowDetailPage() {
  const navigate = useNavigate()
  const { id } = useParams()

  // Données
  const [workflow, setWorkflow] = useState<WorkflowDetail | null>(null)
  const [executions, setExecutions] = useState<WorkflowExecution[]>([])
  const [auditLogs, setAuditLogs] = useState<WorkflowAuditLog[]>([])
  const [totalExecutions, setTotalExecutions] = useState(0)
  const [nextCursor, setNextCursor] = useState<number | null>(null)

  // État UI
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [activeTab, setActiveTab] = useState<Tab>('executions')
  const [expandedExecution, setExpandedExecution] = useState<number | null>(null)
  const [deleteConfirm, setDeleteConfirm] = useState(false)
  const [flushConfirm, setFlushConfirm] = useState(false)
  const [executing, setExecuting] = useState(false)
  const [page, setPage] = useState(1)
  const pageSize = 10

  // Charger les données
  const loadData = useCallback(async () => {
    if (!id) return
    try {
      setLoading(true)
      setError(null)

      const [wf, execsRes, logs] = await Promise.all([
        workflowApi.getWorkflow(Number(id)),
        workflowApi.listExecutions(Number(id), { limit: 100 }),
        workflowApi.listAuditLogs(Number(id), 50),
      ])

      setWorkflow(wf as WorkflowDetail)
      setExecutions(execsRes.executions || [])
      setTotalExecutions(execsRes.total_count || 0)
      setNextCursor(execsRes.next_cursor || null)
      setAuditLogs(logs)
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors du chargement')
    } finally {
      setLoading(false)
    }
  }, [id])

  useEffect(() => {
    loadData()
  }, [loadData])

  // Polling pour les exécutions en cours
  const notifiedExecutionsRef = useRef<Set<number>>(new Set())
  
  useEffect(() => {
    // Vérifier si des exécutions sont en cours
    const hasRunning = executions.some(e => e.status === 'pending' || e.status === 'running')
    
    if (!hasRunning) {
      return
    }
    
    // Polling toutes les 3 secondes
    const interval = setInterval(() => {
      if (id) {
        workflowApi.listExecutions(Number(id), { limit: 100 }).then(res => {
          const newExecutions = res.executions || []
          
          // Vérifier les changements de statut (uniquement les terminées)
          newExecutions.forEach(newExec => {
            // Chercher si cette exécution était en cours
            const oldExec = executions.find(e => e.id === newExec.id)
            
            if (oldExec && 
                (oldExec.status === 'pending' || oldExec.status === 'running') &&
                (newExec.status === 'success' || newExec.status === 'failure' || newExec.status === 'failed') &&
                !notifiedExecutionsRef.current.has(newExec.id)) {
              
              // Marquer comme notifiée
              notifiedExecutionsRef.current.add(newExec.id)
              
              if (newExec.status === 'success') {
                toast.success(`Exécution #${newExec.id} terminée avec succès`, { duration: 4000 })
              } else {
                toast.error(`Exécution #${newExec.id} échouée`, { duration: 5000 })
              }
              setExecuting(false)
            }
          })
          
          setExecutions(newExecutions)
          setTotalExecutions(res.total_count || 0)
        }).catch(() => {})
      }
    }, 3000)
    
    return () => clearInterval(interval)
  }, [executions, id])

  // Activer/Désactiver
  const toggleEnabled = async () => {
    if (!workflow) return
    try {
      if (workflow.is_enabled) {
        await workflowApi.disableWorkflow(workflow.id)
      } else {
        await workflowApi.enableWorkflow(workflow.id)
      }
      loadData()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la modification')
    }
  }

  // Supprimer
  const handleDelete = async () => {
    if (!workflow) return
    try {
      await workflowApi.deleteWorkflow(workflow.id)
      navigate('/workflows')
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la suppression')
    }
  }

  // Exécuter manuellement
  const executeManually = async () => {
    if (!workflow) return
    try {
      setExecuting(true)
      await workflowApi.triggerWorkflow(workflow.id)
      // Attendre un peu puis recharger
      setTimeout(() => {
        loadData()
        setExecuting(false)
      }, 2000)
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de l\'exécution')
      setExecuting(false)
    }
  }

  // Copier l'URL webhook
  const copyWebhookUrl = () => {
    if (!workflow?.webhook_url) return
    navigator.clipboard.writeText(workflow.webhook_url)
  }

  // Supprimer une exécution
  const handleDeleteExecution = async (executionId: number) => {
    if (!workflow) return
    try {
      await workflowApi.deleteExecution(workflow.id, executionId)
      loadData()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la suppression')
    }
  }

  // Flush toutes les exécutions
  const handleFlushExecutions = async () => {
    if (!workflow) return
    try {
      await workflowApi.flushExecutions(workflow.id)
      setFlushConfirm(false)
      loadData()
    } catch (err: any) {
      setError(err?.response?.data?.detail || 'Erreur lors de la suppression')
    }
  }

  // Pagination
  const paginatedExecutions = executions.slice((page - 1) * pageSize, page * pageSize)
  const totalPages = Math.ceil(Math.min(executions.length, 100) / pageSize)

  if (loading) {
    return (
      <div className="relative overflow-hidden min-h-screen">
        <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
        <div className="flex items-center justify-center py-24">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-emerald-500/30 border-t-emerald-500" />
        </div>
      </div>
    )
  }

  if (!workflow) {
    return (
      <div className="relative overflow-hidden min-h-screen">
        <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
        <div className="flex flex-col items-center justify-center py-24">
          <p className="text-zinc-500">Workflow introuvable</p>
          <button onClick={() => navigate('/workflows')} className="mt-4 text-emerald-600 hover:underline">
            Retour aux workflows
          </button>
        </div>
      </div>
    )
  }

  const triggerIcon = (workflow.trigger_connector_slug && connectorIcons[workflow.trigger_connector_slug]) || <Zap className="w-4 h-4" />
  const actionIcon = (workflow.action_connector_slug && connectorIcons[workflow.action_connector_slug]) || <Zap className="w-4 h-4" />
  const triggerName = (workflow.trigger_connector_slug && connectorNames[workflow.trigger_connector_slug]) || workflow.trigger_connector_slug || 'Inconnu'
  const actionName = (workflow.action_connector_slug && connectorNames[workflow.action_connector_slug]) || workflow.action_connector_slug || 'Inconnu'

  // Taux de succès
  const successRate = workflow.run_count > 0 
    ? Math.round((workflow.success_count / workflow.run_count) * 100) 
    : null

  return (
    <div className="relative overflow-hidden min-h-screen">
      {/* Background */}
      <div aria-hidden className="absolute inset-0 -z-30 bg-gradient-to-b from-emerald-50 via-white to-white dark:from-zinc-950 dark:via-zinc-900 dark:to-zinc-900" />
      <div aria-hidden className="absolute inset-x-0 top-0 -z-40 h-[320px] bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.25),_transparent_68%)]" />

      <div className="relative mx-auto max-w-[1600px] px-6 py-6">
        {/* Modal de suppression */}
        {deleteConfirm && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setDeleteConfirm(false)} />
            <div className="absolute inset-x-0 top-1/2 mx-auto w-full max-w-lg -translate-y-1/2 overflow-hidden rounded-3xl border border-red-200/70 bg-white/95 shadow-2xl dark:border-red-900/60 dark:bg-zinc-950/95">
              <div className="flex items-center justify-between border-b border-red-200/70 bg-red-50/60 px-6 py-4 dark:border-red-900/60 dark:bg-red-950/40">
                <div>
                  <h2 className="text-lg font-semibold text-red-700 dark:text-red-200">Supprimer le workflow</h2>
                  <p className="text-sm text-red-600/80 dark:text-red-300/80">Cette action est irréversible.</p>
                </div>
              </div>
              <div className="space-y-4 px-6 py-6">
                <p className="text-sm text-zinc-700 dark:text-zinc-200">
                  Êtes-vous sûr de vouloir supprimer <span className="font-semibold">{workflow.name}</span> ?
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setDeleteConfirm(false)}
                    className="rounded-xl px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleDelete}
                    className="rounded-xl bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
                  >
                    Supprimer
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Modal flush exécutions */}
        {flushConfirm && (
          <div className="fixed inset-0 z-50">
            <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={() => setFlushConfirm(false)} />
            <div className="absolute inset-x-0 top-1/2 mx-auto w-full max-w-lg -translate-y-1/2 overflow-hidden rounded-3xl border border-amber-200/70 bg-white/95 shadow-2xl dark:border-amber-900/60 dark:bg-zinc-950/95">
              <div className="flex items-center justify-between border-b border-amber-200/70 bg-amber-50/60 px-6 py-4 dark:border-amber-900/60 dark:bg-amber-950/40">
                <div>
                  <h2 className="text-lg font-semibold text-amber-700 dark:text-amber-200">Vider l'historique</h2>
                  <p className="text-sm text-amber-600/80 dark:text-amber-300/80">Supprimer toutes les exécutions</p>
                </div>
              </div>
              <div className="space-y-4 px-6 py-6">
                <p className="text-sm text-zinc-700 dark:text-zinc-200">
                  Êtes-vous sûr de vouloir supprimer les <span className="font-semibold">{totalExecutions} exécutions</span> ?
                  Les compteurs seront également remis à zéro.
                </p>
                <div className="flex justify-end gap-3">
                  <button
                    onClick={() => setFlushConfirm(false)}
                    className="rounded-xl px-4 py-2 text-sm font-medium text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800"
                  >
                    Annuler
                  </button>
                  <button
                    onClick={handleFlushExecutions}
                    className="rounded-xl bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
                  >
                    Vider
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* Erreur */}
        {error && (
          <div className="mb-6 rounded-2xl border border-red-200/70 bg-red-50/70 px-4 py-3 text-sm text-red-700 dark:border-red-900/60 dark:bg-red-950/40 dark:text-red-200">
            {error}
            <button onClick={() => setError(null)} className="ml-2 underline">Fermer</button>
          </div>
        )}

        {/* En-tête */}
        <div className="mb-6 flex flex-wrap items-start justify-between gap-4">
          <div className="flex items-start gap-4">
            <button
              onClick={() => navigate('/workflows')}
              className="mt-1 rounded-xl p-2 text-zinc-500 hover:bg-zinc-100 dark:text-zinc-400 dark:hover:bg-zinc-800"
            >
              <ArrowLeft className="w-5 h-5" />
            </button>
            <div>
              <div className="flex items-center gap-3">
                <h1 className="text-xl font-bold text-zinc-900 dark:text-white">{workflow.name}</h1>
                <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${
                  workflow.is_enabled
                    ? 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/50 dark:text-emerald-300'
                    : 'bg-zinc-100 text-zinc-600 dark:bg-zinc-800 dark:text-zinc-400'
                }`}>
                  {workflow.is_enabled ? 'Actif' : 'Inactif'}
                </span>
              </div>
              <p className="text-sm text-zinc-500 dark:text-zinc-400 mt-1">
                Agent: {workflow.agent_name}
              </p>
              {workflow.description && (
                <p className="text-sm text-zinc-600 dark:text-zinc-300 mt-2">{workflow.description}</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex items-center gap-2">
            {workflow.trigger_connector_slug === 'manual' && (
              <button
                onClick={executeManually}
                disabled={executing || !workflow.is_enabled}
                className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-500 disabled:opacity-50"
              >
                {executing ? (
                  <RefreshCw className="w-4 h-4 animate-spin" />
                ) : (
                  <Play className="w-4 h-4" />
                )}
                Exécuter
              </button>
            )}
            <button
              onClick={toggleEnabled}
              className={`inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-medium ${
                workflow.is_enabled
                  ? 'bg-amber-100 text-amber-700 hover:bg-amber-200 dark:bg-amber-900/30 dark:text-amber-300'
                  : 'bg-emerald-100 text-emerald-700 hover:bg-emerald-200 dark:bg-emerald-900/30 dark:text-emerald-300'
              }`}
            >
              {workflow.is_enabled ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
              {workflow.is_enabled ? 'Désactiver' : 'Activer'}
            </button>
            <button
              onClick={() => navigate(`/workflows/${workflow.id}/edit`)}
              className="inline-flex items-center gap-2 rounded-xl bg-zinc-100 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
            >
              <Edit className="w-4 h-4" />
              Modifier
            </button>
            <button
              onClick={() => setDeleteConfirm(true)}
              className="inline-flex items-center gap-2 rounded-xl bg-red-100 px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-200 dark:bg-red-900/30 dark:text-red-300"
            >
              <Trash2 className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* Statistiques */}
        <section className="mb-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-2xl border border-zinc-200/80 bg-white/70 p-4 backdrop-blur-sm dark:border-zinc-700/80 dark:bg-zinc-900/70">
            <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">Exécutions</p>
            <p className="mt-1 text-2xl font-bold text-zinc-900 dark:text-white">{numberFormatter.format(workflow.run_count)}</p>
          </div>
          <div className="rounded-2xl border border-emerald-200/80 bg-emerald-50/70 p-4 dark:border-emerald-900/40 dark:bg-emerald-900/20">
            <p className="text-xs font-medium uppercase tracking-wider text-emerald-600 dark:text-emerald-400">Succès</p>
            <p className="mt-1 text-2xl font-bold text-emerald-700 dark:text-emerald-300">{numberFormatter.format(workflow.success_count)}</p>
          </div>
          <div className="rounded-2xl border border-red-200/80 bg-red-50/70 p-4 dark:border-red-900/40 dark:bg-red-900/20">
            <p className="text-xs font-medium uppercase tracking-wider text-red-600 dark:text-red-400">Échecs</p>
            <p className="mt-1 text-2xl font-bold text-red-700 dark:text-red-300">{numberFormatter.format(workflow.failure_count ?? 0)}</p>
          </div>
          <div className="rounded-2xl border border-zinc-200/80 bg-white/70 p-4 backdrop-blur-sm dark:border-zinc-700/80 dark:bg-zinc-900/70">
            <p className="text-xs font-medium uppercase tracking-wider text-zinc-500">Taux de succès</p>
            <p className="mt-1 text-2xl font-bold text-zinc-900 dark:text-white">{successRate != null ? `${successRate}%` : '—'}</p>
          </div>
        </section>

        {/* Configuration */}
        <section className="mb-6 grid gap-4 sm:grid-cols-2">
          {/* Déclencheur */}
          <div className="rounded-2xl border border-blue-200/80 bg-blue-50/70 p-5 dark:border-blue-900/40 dark:bg-blue-900/20">
            <div className="flex items-center gap-3 mb-3">
              <div className="rounded-lg bg-blue-500 p-2 text-white">{triggerIcon}</div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-blue-600 dark:text-blue-400">Déclencheur</p>
                <p className="font-semibold text-blue-900 dark:text-blue-100">{triggerName}</p>
              </div>
            </div>
            {workflow.trigger_config && Object.keys(workflow.trigger_config).length > 0 && (
              <div className="mt-3 rounded-lg bg-blue-100/50 p-3 dark:bg-blue-900/30">
                <pre className="text-xs text-blue-800 dark:text-blue-200 whitespace-pre-wrap">
                  {JSON.stringify(workflow.trigger_config, null, 2)}
                </pre>
              </div>
            )}
            {workflow.next_run_at && (
              <p className="mt-3 text-xs text-blue-700 dark:text-blue-300">
                Prochaine exécution: {dateFormatter.format(new Date(workflow.next_run_at))}
              </p>
            )}
          </div>

          {/* Action */}
          <div className="rounded-2xl border border-purple-200/80 bg-purple-50/70 p-5 dark:border-purple-900/40 dark:bg-purple-900/20">
            <div className="flex items-center gap-3 mb-3">
              <div className="rounded-lg bg-purple-500 p-2 text-white">{actionIcon}</div>
              <div>
                <p className="text-xs font-medium uppercase tracking-wider text-purple-600 dark:text-purple-400">Action</p>
                <p className="font-semibold text-purple-900 dark:text-purple-100">{actionName}</p>
              </div>
            </div>
            {workflow.action_config && Object.keys(workflow.action_config).length > 0 && (
              <div className="mt-3 rounded-lg bg-purple-100/50 p-3 dark:bg-purple-900/30">
                <pre className="text-xs text-purple-800 dark:text-purple-200 whitespace-pre-wrap">
                  {JSON.stringify(workflow.action_config, null, 2)}
                </pre>
              </div>
            )}
          </div>
        </section>

        {/* URL Webhook */}
        {workflow.webhook_url && (
          <section className="mb-6 rounded-2xl border border-zinc-200/80 bg-white/70 p-5 backdrop-blur-sm dark:border-zinc-700/80 dark:bg-zinc-900/70">
            <p className="text-sm font-medium text-zinc-700 dark:text-zinc-300 mb-2">URL Webhook</p>
            <div className="flex items-center gap-2">
              <code className="flex-1 rounded-lg bg-zinc-100 px-3 py-2 text-sm text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200 overflow-x-auto">
                {workflow.webhook_url}
              </code>
              <button
                onClick={copyWebhookUrl}
                className="shrink-0 rounded-lg bg-zinc-100 p-2 text-zinc-600 hover:bg-zinc-200 dark:bg-zinc-800 dark:text-zinc-300 dark:hover:bg-zinc-700"
                title="Copier"
              >
                <Copy className="w-4 h-4" />
              </button>
            </div>
          </section>
        )}

        {/* Onglets */}
        <div className="mb-4 flex items-center gap-1 border-b border-zinc-200 dark:border-zinc-700">
          <button
            onClick={() => setActiveTab('executions')}
            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
              activeTab === 'executions'
                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
            }`}
          >
            <History className="w-4 h-4" />
            Exécutions
          </button>
          <button
            onClick={() => setActiveTab('audit')}
            className={`inline-flex items-center gap-2 px-4 py-3 text-sm font-medium border-b-2 transition ${
              activeTab === 'audit'
                ? 'border-emerald-500 text-emerald-600 dark:text-emerald-400'
                : 'border-transparent text-zinc-500 hover:text-zinc-700 dark:text-zinc-400 dark:hover:text-zinc-200'
            }`}
          >
            <FileText className="w-4 h-4" />
            Audit
          </button>
        </div>

        {/* Contenu des onglets */}
        {activeTab === 'executions' && (
          <div className="space-y-3">
            {/* Header avec total et bouton flush */}
            <div className="flex items-center justify-between">
              <p className="text-sm text-zinc-500 dark:text-zinc-400">
                {totalExecutions > 0 ? (
                  <>
                    {totalExecutions} exécution{totalExecutions > 1 ? 's' : ''} au total
                    {totalExecutions > 100 && (
                      <span className="ml-1 text-xs">(affichage des 100 dernières)</span>
                    )}
                  </>
                ) : (
                  'Aucune exécution'
                )}
              </p>
              {executions.length > 0 && (
                <button
                  onClick={() => setFlushConfirm(true)}
                  className="text-xs text-red-500 hover:text-red-600 dark:text-red-400"
                >
                  Vider l'historique
                </button>
              )}
            </div>

            {executions.length === 0 ? (
              <div className="rounded-2xl border border-zinc-200/80 bg-white/70 p-8 text-center backdrop-blur-sm dark:border-zinc-700/80 dark:bg-zinc-900/70">
                <History className="mx-auto h-10 w-10 text-zinc-300 dark:text-zinc-600" />
                <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">Aucune exécution pour le moment</p>
              </div>
            ) : (
              <>
                {paginatedExecutions.map((exec) => (
                  <ExecutionCard
                    key={exec.id}
                    execution={exec}
                    isExpanded={expandedExecution === exec.id}
                    onToggle={() => setExpandedExecution(expandedExecution === exec.id ? null : exec.id)}
                    onDelete={() => handleDeleteExecution(exec.id)}
                  />
                ))}

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-center gap-2 pt-4">
                    <button
                      onClick={() => setPage(Math.max(1, page - 1))}
                      disabled={page === 1}
                      className="rounded-lg px-3 py-1.5 text-sm text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 dark:text-zinc-300 dark:hover:bg-zinc-800"
                    >
                      ←
                    </button>
                    {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                      <button
                        key={p}
                        onClick={() => setPage(p)}
                        className={`rounded-lg px-3 py-1.5 text-sm ${
                          p === page
                            ? 'bg-emerald-500 text-white'
                            : 'text-zinc-600 hover:bg-zinc-100 dark:text-zinc-300 dark:hover:bg-zinc-800'
                        }`}
                      >
                        {p}
                      </button>
                    ))}
                    <button
                      onClick={() => setPage(Math.min(totalPages, page + 1))}
                      disabled={page === totalPages}
                      className="rounded-lg px-3 py-1.5 text-sm text-zinc-600 hover:bg-zinc-100 disabled:opacity-50 dark:text-zinc-300 dark:hover:bg-zinc-800"
                    >
                      →
                    </button>
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {activeTab === 'audit' && (
          <div className="space-y-2">
            {auditLogs.length === 0 ? (
              <div className="rounded-2xl border border-zinc-200/80 bg-white/70 p-8 text-center backdrop-blur-sm dark:border-zinc-700/80 dark:bg-zinc-900/70">
                <FileText className="mx-auto h-10 w-10 text-zinc-300 dark:text-zinc-600" />
                <p className="mt-3 text-sm text-zinc-500 dark:text-zinc-400">Aucun log d'audit</p>
              </div>
            ) : (
              auditLogs.map((log) => (
                <div
                  key={log.id}
                  className="rounded-xl border border-zinc-200/80 bg-white/70 p-4 backdrop-blur-sm dark:border-zinc-700/80 dark:bg-zinc-900/70"
                >
                  <div className="flex items-start justify-between gap-4">
                    <div>
                      <p className="text-sm font-medium text-zinc-900 dark:text-white">{log.action}</p>
                      {log.details && (
                        <p className="mt-1 text-xs text-zinc-500 dark:text-zinc-400">{log.details}</p>
                      )}
                    </div>
                    <p className="text-xs text-zinc-400 dark:text-zinc-500 whitespace-nowrap">
                      {dateFormatter.format(new Date(log.created_at))}
                    </p>
                  </div>
                </div>
              ))
            )}
          </div>
        )}
      </div>

      {/* Toast notifications */}
      <Toaster
        position="top-right"
        toastOptions={{
          duration: 4000,
          style: {
            background: '#18181b',
            color: '#fff',
          },
        }}
      />
    </div>
  )
}

// Carte d'exécution
function ExecutionCard({
  execution,
  isExpanded,
  onToggle,
  onDelete,
}: {
  execution: WorkflowExecution
  isExpanded: boolean
  onToggle: () => void
  onDelete: () => void
}) {
  const statusIcon = {
    success: <CheckCircle className="w-4 h-4 text-emerald-500" />,
    failure: <XCircle className="w-4 h-4 text-red-500" />,
    failed: <XCircle className="w-4 h-4 text-red-500" />,
    running: <RefreshCw className="w-4 h-4 text-blue-500 animate-spin" />,
    pending: <Clock className="w-4 h-4 text-amber-500" />,
  }[execution.status] || <AlertTriangle className="w-4 h-4 text-zinc-400" />

  const statusLabel = {
    success: 'Succès',
    failure: 'Échec',
    failed: 'Échec',
    running: 'En cours',
    pending: 'En attente',
  }[execution.status] || execution.status

  const statusColor = {
    success: 'bg-emerald-100 text-emerald-700 dark:bg-emerald-900/30 dark:text-emerald-300',
    failure: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    failed: 'bg-red-100 text-red-700 dark:bg-red-900/30 dark:text-red-300',
    running: 'bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-300',
    pending: 'bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300',
  }[execution.status] || 'bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300'

  return (
    <div className="rounded-xl border border-zinc-200/80 bg-white/70 backdrop-blur-sm dark:border-zinc-700/80 dark:bg-zinc-900/70 overflow-hidden">
      <button
        onClick={onToggle}
        className="w-full flex items-center justify-between p-4 text-left hover:bg-zinc-50 dark:hover:bg-zinc-800/50"
      >
        <div className="flex items-center gap-3">
          {statusIcon}
          <div>
            <p className="text-sm font-medium text-zinc-900 dark:text-white">
              Exécution #{execution.id}
            </p>
            <p className="text-xs text-zinc-500 dark:text-zinc-400">
              {dateFormatter.format(new Date(execution.started_at))}
              {execution.duration_ms && ` • ${(execution.duration_ms / 1000).toFixed(2)}s`}
            </p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          <span className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-xs font-medium ${statusColor}`}>
            {statusLabel}
          </span>
          {isExpanded ? <ChevronUp className="w-4 h-4 text-zinc-400" /> : <ChevronDown className="w-4 h-4 text-zinc-400" />}
        </div>
      </button>

      {isExpanded && (
        <div className="border-t border-zinc-200 p-4 space-y-4 dark:border-zinc-700">
          {/* Input */}
          {execution.input_data && (
            <div>
              <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">Entrée</p>
              <pre className="rounded-lg bg-zinc-100 p-3 text-xs text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200 overflow-x-auto whitespace-pre-wrap">
                {JSON.stringify(execution.input_data, null, 2)}
              </pre>
            </div>
          )}

          {/* Output */}
          {execution.output_data && (
            <div>
              <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400 mb-1">Sortie</p>
              <pre className="rounded-lg bg-zinc-100 p-3 text-xs text-zinc-800 dark:bg-zinc-800 dark:text-zinc-200 overflow-x-auto whitespace-pre-wrap">
                {typeof execution.output_data === 'string' 
                  ? execution.output_data 
                  : JSON.stringify(execution.output_data, null, 2)}
              </pre>
            </div>
          )}

          {/* Erreur */}
          {execution.error_message && (
            <div>
              <p className="text-xs font-medium text-red-500 dark:text-red-400 mb-1">Erreur</p>
              <pre className="rounded-lg bg-red-50 p-3 text-xs text-red-700 dark:bg-red-900/20 dark:text-red-300 overflow-x-auto whitespace-pre-wrap">
                {execution.error_message}
              </pre>
            </div>
          )}

          {/* Bouton supprimer */}
          <div className="pt-2 border-t border-zinc-200 dark:border-zinc-700">
            <button
              onClick={(e) => { e.stopPropagation(); onDelete(); }}
              className="text-xs text-red-500 hover:text-red-600 dark:text-red-400"
            >
              Supprimer cette exécution
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
